'use client';

import { Input, Label } from '@routly/ui';
import type { Behavior } from '@/lib/types';

/**
 * The five numbers that decide what becomes an interval.
 *
 * Each is labelled with the mistake it prevents rather than its field name.
 * `maxInactiveS` means nothing to an operator; "a stop shorter than this does
 * not end the interval" is the thing they are actually setting, and the number
 * that turned one delivery round into a dozen trips when it was wrong.
 */
const FIELDS: { key: keyof Behavior; label: string; hint: string; nullable?: boolean }[] = [
  {
    key: 'minActiveS',
    label: 'Hold for',
    hint: 'How long the condition must be true before an interval opens. One GPS sample over the limit is a spike, not speeding.',
  },
  {
    key: 'maxInactiveS',
    label: 'Merge gaps up to',
    hint: 'A break shorter than this does not end the interval. A stop at a light is not the end of a trip.',
  },
  {
    key: 'minDurationS',
    label: 'Discard shorter than',
    hint: 'Closed intervals below this are dropped rather than stored.',
  },
  {
    key: 'maxDurationS',
    label: 'Discard longer than',
    hint: 'Past this it is a different thing — an hour of standing still is a parking, not a stop. Blank for no limit.',
    nullable: true,
  },
  {
    key: 'maxActiveS',
    label: 'Force-close after',
    hint: 'A sensor stuck on would otherwise hold an interval open for ever, and one that never closes never becomes a row. Blank for no limit.',
    nullable: true,
  },
];

interface BehaviorFieldsProps {
  behavior: Behavior;
  onChange: (behavior: Behavior) => void;
}

export function BehaviorFields({ behavior, onChange }: BehaviorFieldsProps) {
  const set = (key: keyof Behavior, raw: string, nullable: boolean) => {
    const value = raw.trim() === '' ? (nullable ? null : 0) : Number(raw);
    if (value !== null && !Number.isFinite(value)) return;
    onChange({ ...behavior, [key]: value });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {FIELDS.map((field) => (
        <div key={field.key} className="space-y-1">
          <Label htmlFor={field.key}>
            {field.label} <span className="text-muted-foreground">(seconds)</span>
          </Label>
          <Input
            id={field.key}
            inputMode="numeric"
            value={behavior[field.key] ?? ''}
            placeholder={field.nullable ? 'no limit' : '0'}
            onChange={(event) => set(field.key, event.target.value, field.nullable ?? false)}
          />
          <p className="text-xs text-muted-foreground">{field.hint}</p>
        </div>
      ))}

      <div className="space-y-1">
        <Label htmlFor="minDistanceKm">
          Discard under <span className="text-muted-foreground">(km)</span>
        </Label>
        <Input
          id="minDistanceKm"
          inputMode="decimal"
          value={behavior.minDistanceKm ?? ''}
          placeholder="no limit"
          onChange={(event) => set('minDistanceKm', event.target.value, true)}
        />
        <p className="text-xs text-muted-foreground">
          A duration limit alone cannot say what a trip is: shunting around a yard clears any time
          limit without going anywhere. Needs a distance counter to measure.
        </p>
      </div>
    </div>
  );
}
